import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { cache, CACHE_CONFIG } from '../utils/cacheUtils';
import { useAdmin } from '../hooks/useAdmin';
import Modal from '../components/Modal';
import SEO from '../components/SEO';
import { blogSlug } from '../utils/slug';

export default function Blog() {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adminModalOpen, setAdminModalOpen] = useState(false);
  const [form, setForm] = useState({ title: '', content: '' });
  const [posting, setPosting] = useState(false);
  const [pendingDeleteId, setPendingDeleteId] = useState(null);
  const [toast, setToast] = useState(null);

  const { adminMode } = useAdmin();

  const showToast = (msg, isError = false) => {
    setToast({ msg, isError });
    setTimeout(() => setToast(null), 3500);
  };

  const fetchBlogs = async (fromCache = true) => {
    if (fromCache) {
      const cached = cache.get(CACHE_CONFIG.BLOGS);
      if (cached) {
        setBlogs(cached.data);
        setLoading(false);
        if (cached.isStale) fetchBlogs(false);
        return;
      }
    }
    setLoading(true);
    try {
      const { data, error } = await supabase.from('blogs').select('*').order('id', { ascending: false });
      if (error) throw error;
      setBlogs(data || []);
      cache.set(CACHE_CONFIG.BLOGS, data || []);
    } catch {
      setBlogs([]);
    }
    setLoading(false);
  };

  useEffect(() => { fetchBlogs(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setPosting(true);
    try {
      const { error } = await supabase.from('blogs').insert([{ title: form.title, content: form.content }]);
      if (error) throw error;
      cache.clear(CACHE_CONFIG.BLOGS);
      await fetchBlogs(false);
      setForm({ title: '', content: '' });
      setAdminModalOpen(false);
      showToast('Blog posted!');
    } catch (err) {
      showToast(err.message || 'Failed to post', true);
    }
    setPosting(false);
  };

  const confirmDelete = async () => {
    const id = pendingDeleteId;
    setPendingDeleteId(null);
    const { error } = await supabase.from('blogs').delete().eq('id', id);
    if (error) showToast('Failed to delete', true);
    else {
      setBlogs((prev) => prev.filter((b) => b.id !== id));
      cache.clear(CACHE_CONFIG.BLOGS);
    }
  };

  return (
    <div className="page container">
      <SEO
        title="Blog | Joan Christine Nyawira — Physiotherapy, Marketing & Life"
        description="Read Joan Christine Nyawira's latest posts on physiotherapy, digital marketing, and everyday insights from Jowey's."
        path="/blog"
      />
      <div className="glass-card" style={{ maxWidth: 760, margin: '0 auto' }}>
        <div className="section-header" style={{ marginBottom: '1.5rem' }}>
          <h1 style={{ fontSize: '2rem' }}>Blog</h1>
          {adminMode && (
            <div className="admin-bar">
              <button className="btn btn-ghost btn-sm" onClick={() => setAdminModalOpen(true)}>
                + New Post
              </button>
            </div>
          )}
        </div>

        {loading ? (
          <div className="loading-state">Loading posts…</div>
        ) : blogs.length === 0 ? (
          <div className="empty-state">No blog posts yet.</div>
        ) : (
          blogs.map((blog) => (
            <article key={blog.id} className="blog-post">
              {adminMode && (
                <button
                  className="btn btn-danger btn-sm"
                  style={{ position: 'absolute', top: '1.5rem', right: 0 }}
                  onClick={() => setPendingDeleteId(blog.id)}
                >
                  Delete
                </button>
              )}
              <h2><Link to={`/blog/${blogSlug(blog)}`}>{blog.title}</Link></h2>
              <div className="blog-date">
                {blog.created_at
                  ? new Date(blog.created_at).toLocaleDateString('en-KE', { dateStyle: 'long' })
                  : 'No date'}
              </div>
              <div className="blog-content">{blog.content}</div>
            </article>
          ))
        )}
      </div>

      <Modal open={adminMode && adminModalOpen} onClose={() => setAdminModalOpen(false)}>
        <h3 style={{ fontFamily: 'Syne', marginBottom: '1.25rem' }}>New Blog Post</h3>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <input className="form-input" placeholder="Title" value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })} required />
          </div>
          <div className="form-group">
            <textarea className="form-input" placeholder="Content" rows={6} value={form.content}
              onChange={(e) => setForm({ ...form, content: e.target.value })} required />
          </div>
          <button type="submit" className="btn btn-primary" disabled={posting} style={{ width: '100%' }}>
            {posting ? 'Posting…' : 'Publish'}
          </button>
        </form>
      </Modal>

      <Modal open={!!pendingDeleteId} onClose={() => setPendingDeleteId(null)}>
        <p style={{ marginBottom: '1.5rem', textAlign: 'center' }}>Delete this blog post?</p>
        <div className="form-actions">
          <button className="btn btn-danger" onClick={confirmDelete}>Delete</button>
          <button className="btn btn-ghost" onClick={() => setPendingDeleteId(null)}>Cancel</button>
        </div>
      </Modal>

      {toast && (
        <div className={`toast${toast.isError ? ' toast-error' : ''}`}>{toast.msg}</div>
      )}
    </div>
  );
}
